var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
// useStreamingResponse.ts - Enhanced server debugging
import { useReducer, useCallback } from 'react';
const initialState = {
    contentBlocks: [],
    citations: [],
    isStreaming: false,
    error: null,
    newblock: false,
    errorblock: false,
    replyId: '',
    threadId: '',
};
/**
 * Reducer for managing complex streaming response state
 * Handles all state transitions for AI agent streaming responses
 */
function streamingReducer(state, action) {
    switch (action.type) {
        case 'RESET':
            // Complete reset - used when user clears/refreshes chat
            return Object.assign({}, initialState);
        case 'START_STREAM':
            // Prepare for a new streaming request within the same conversation
            return Object.assign(Object.assign({}, state), { contentBlocks: [], citations: [], isStreaming: true, error: null, newblock: false, errorblock: false });
        case 'APPEND_TEXT':
            // Add new text content to the current text block or create a new one
            const lastBlock = state.contentBlocks[state.contentBlocks.length - 1];
            if (lastBlock && lastBlock.type === 'text') {
                // Append to existing text block
                const updatedBlocks = [...state.contentBlocks];
                updatedBlocks[updatedBlocks.length - 1] = Object.assign(Object.assign({}, lastBlock), { content: lastBlock.content + action.payload });
                return Object.assign(Object.assign({}, state), { contentBlocks: updatedBlocks });
            }
            else {
                // Create new text block
                const newTextBlock = {
                    id: `text-${crypto.randomUUID()}`,
                    type: 'text',
                    content: action.payload,
                    timestamp: Date.now(),
                };
                return Object.assign(Object.assign({}, state), { contentBlocks: [...state.contentBlocks, newTextBlock] });
            }
        case 'ADD_ACTION':
            // Add an action block
            const newActionBlock = {
                id: `action-${action.payload.label}`,
                type: 'action',
                content: action.payload.action,
                timestamp: Date.now(),
                label: action.payload.label,
            };
            return Object.assign(Object.assign({}, state), { contentBlocks: [...state.contentBlocks, newActionBlock] });
        case 'ADD_INPUT':
            // Add an input block for agent questions
            const newInputBlock = {
                id: `input-${crypto.randomUUID()}`,
                type: 'input',
                content: action.payload,
                timestamp: Date.now(),
            };
            return Object.assign(Object.assign({}, state), { contentBlocks: [...state.contentBlocks, newInputBlock] });
        case 'ADD_GRAPH':
            // Add a graph content block
            const newGraphBlock = {
                id: `graph-${crypto.randomUUID()}`,
                type: 'graph',
                content: action.payload,
                timestamp: Date.now(),
            };
            return Object.assign(Object.assign({}, state), { contentBlocks: [...state.contentBlocks, newGraphBlock] });
        case 'ERROR_OCCURRED':
            // Replace content with error message when error occurs during streaming
            // This prevents confusing mixed content (partial response + error message)
            const errorBlock = {
                id: `error-${crypto.randomUUID()}`,
                type: 'text',
                content: action.payload,
                timestamp: Date.now(),
            };
            return Object.assign(Object.assign({}, state), { contentBlocks: [errorBlock], errorblock: true, error: action.payload });
        case 'SET_CITATIONS':
            // Replace all citations - used when we have a complete citation set
            return Object.assign(Object.assign({}, state), { citations: action.payload });
        case 'APPEND_CITATIONS':
            // Add new citations to existing ones - used for incremental citation updates
            return Object.assign(Object.assign({}, state), { citations: [...state.citations, ...action.payload] });
        case 'SET_REPLY_ID':
            // Set the conversation reply ID from the AI response
            return Object.assign(Object.assign({}, state), { replyId: action.payload });
        case 'SET_THREAD_ID':
            // Set the thread ID from the starting status message
            return Object.assign(Object.assign({}, state), { threadId: action.payload });
        case 'SET_NEW_BLOCK':
            // Indicate whether we're starting a new content block
            return Object.assign(Object.assign({}, state), { newblock: action.payload });
        case 'SET_ERROR_BLOCK':
            // Mark current content as an error block
            return Object.assign(Object.assign({}, state), { errorblock: action.payload });
        case 'SET_ERROR':
            // Set general error state for the streaming operation
            return Object.assign(Object.assign({}, state), { error: action.payload });
        case 'STOP_STREAM':
            // Mark streaming as complete - called when stream ends (success or failure)
            return Object.assign(Object.assign({}, state), { isStreaming: false });
        default:
            return state;
    }
}
export const useStreamingResponse = () => {
    const [state, dispatch] = useReducer(streamingReducer, initialState);
    const handleStream = useCallback((...args_1) => __awaiter(void 0, [...args_1], void 0, function* (options = {}, streamUrl) {
        var _a, _b;
        dispatch({ type: 'START_STREAM' });
        try {
            if (!streamUrl) {
                throw new Error('Agent URL must be provided via config.agent_url');
            }
            const response = yield fetch(streamUrl, {
                method: 'POST',
                headers: Object.assign({ 'Content-Type': 'application/json', 'Accept': 'text/event-stream' }, options.headers),
                body: options.body ? JSON.stringify(options.body) : undefined,
            });
            if (((_a = options.body) === null || _a === void 0 ? void 0 : _a.reply_id) !== undefined) {
                dispatch({ type: 'SET_REPLY_ID', payload: '' });
            }
            const reader = (_b = response.body) === null || _b === void 0 ? void 0 : _b.getReader();
            if (!reader) {
                throw new Error('No reader available');
            }
            const decoder = new TextDecoder();
            while (true) {
                const { done, value } = yield reader.read();
                if (done) {
                    break;
                }
                const chunk = decoder.decode(value);
                const lines = chunk.split('\n');
                for (const line of lines) {
                    if (line.trim() === '')
                        continue;
                    try {
                        const data = JSON.parse(line);
                        //console.log('   📝 Received data:', data);
                        if (data.text && data.text !== '') {
                            dispatch({ type: 'APPEND_TEXT', payload: data.text });
                            dispatch({ type: 'SET_NEW_BLOCK', payload: false });
                        }
                        if (data.status && data.status === 'break') {
                            dispatch({ type: 'APPEND_TEXT', payload: '\n\n' });
                            dispatch({ type: 'SET_NEW_BLOCK', payload: true });
                        }
                        if (data.status && data.status === 'starting' && data.thread_id) {
                            dispatch({ type: 'SET_THREAD_ID', payload: data.thread_id });
                        }
                        if (data.input) {
                            dispatch({ type: 'ADD_INPUT', payload: data.input });
                        }
                        if (data.reply_id) {
                            dispatch({ type: 'SET_REPLY_ID', payload: data.reply_id || '' });
                        }
                        if (data.graph) {
                            dispatch({ type: 'ADD_GRAPH', payload: data.graph });
                        }
                        if (data.action) {
                            dispatch({ type: 'ADD_ACTION', payload: { action: data.action, label: data.label } });
                        }
                        if (data.error) {
                            dispatch({ type: 'ERROR_OCCURRED', payload: data.error });
                        }
                        // Keep citations separate from content
                        if (Array.isArray(data.citations) && data.citations.length > 0) {
                            const formattedCitations = data.citations.map((citation, index) => ({
                                id: `citation-${index}`,
                                uri: citation.uri,
                                title: citation.title,
                                filename: citation.filename,
                                publisher: citation.publisher,
                            }));
                            dispatch({ type: 'APPEND_CITATIONS', payload: formattedCitations });
                        }
                        if (data.detail) {
                            console.log('   📝 Detail:', data.detail);
                            dispatch({ type: 'ERROR_OCCURRED', payload: data.detail });
                        }
                    }
                    catch (e) {
                        console.log('   ⚠️ Not JSON, raw line:', line);
                        // Try Server-Sent Events format
                        if (line.startsWith('Detail: ')) {
                            try {
                                const jsonStr = line.substring(6); // Remove 'data: '
                                const data = JSON.parse(jsonStr);
                                if (data.detail) {
                                    dispatch({ type: 'ERROR_OCCURRED', payload: data.detail });
                                }
                                if (data.error) {
                                    dispatch({ type: 'ERROR_OCCURRED', payload: data.error });
                                }
                                if (data.citations && data.citations.length > 0) {
                                    const formattedCitations = data.citations.map((citation, index) => ({
                                        id: `citation-${index}`,
                                        uri: citation.uri,
                                        title: citation.title,
                                        filename: citation.filename,
                                        publisher: citation.publisher,
                                    }));
                                    dispatch({ type: 'APPEND_CITATIONS', payload: formattedCitations });
                                }
                            }
                            catch (sseError) {
                                console.log('   ❌ SSE parse failed:', sseError);
                                dispatch({ type: 'ERROR_OCCURRED', payload: sseError instanceof Error ? sseError.message : String(sseError) });
                            }
                        }
                        else {
                            console.log('   ❌ JSON parse failed:', e);
                            dispatch({ type: 'ERROR_OCCURRED', payload: e instanceof Error ? e.message : String(e) });
                        }
                    }
                }
            }
        }
        catch (error) {
            console.error('❌ Stream error:', error);
            dispatch({ type: 'ERROR_OCCURRED', payload: error.message });
            dispatch({ type: 'SET_ERROR', payload: error.message || 'Error in streaming' });
        }
        finally {
            dispatch({ type: 'STOP_STREAM' });
        }
    }), []);
    const resetState = useCallback(() => {
        dispatch({ type: 'RESET' });
    }, []);
    return {
        contentBlocks: state.contentBlocks,
        citations: state.citations,
        isStreaming: state.isStreaming,
        error: state.error,
        newblock: state.newblock,
        errorblock: state.errorblock,
        replyId: state.replyId,
        threadId: state.threadId,
        startStream: handleStream,
        resetState,
    };
};
